import React, { useEffect } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const ConsultingPage: React.FC = () => {
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  useEffect(() => {
    document.title = 'Consulting | Kevyn Franco';
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: 'white',
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 6 } }}>
          <Box sx={{ mb: 6 }}>
            {/* Line 1 */}
            <Box
              sx={{
                display: 'inline-block',
                backgroundColor: '#dc143c',
                px: { xs: 1.5, md: 2 },
                py: { xs: 0.5, md: 1 },
                mb: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: '"Impact", "Arial Black", sans-serif',
                  fontSize: { xs: '2rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                  lineHeight: 1,
                  fontWeight: 900,
                  color: '#1a1a1a',
                  letterSpacing: '-0.02em',
                }}
              >
                CONSULTING
              </Box>
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: { xs: '1.125rem', md: '1.5rem' },
              lineHeight: 1.7,
              maxWidth: '800px',
              mb: 4,
              fontFamily: '"Arial", sans-serif',
              color: '#1a1a1a',
            }}
          >
            I work with startups and enterprises that need senior-level AI and Python expertise —
            without the overhead of building a team from scratch.
          </Typography>
        </Container>
      </Box>

      {/* What I Do */}
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 6,
              textTransform: 'uppercase',
            }}
          >
            WHAT I DO
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[
              {
                title: 'AI Architecture',
                desc: 'Design and implement production AI systems using LangChain, LLMs, RAG pipelines, and vector databases.',
              },
              {
                title: 'Python Backend Development',
                desc: 'Build scalable APIs and microservices with FastAPI, Django, and Flask. Deploy on AWS, GCP, or Azure.',
              },
              {
                title: 'Technical Advisory',
                desc: 'Architecture reviews, AI strategy, code review, and mentoring for engineering teams.',
              },
            ].map((service) => (
              <Box key={service.title}>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    fontWeight: 900,
                    fontFamily: '"Arial Black", sans-serif',
                    mb: 1,
                    color: 'white',
                  }}
                >
                  {service.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    lineHeight: 1.7,
                    color: '#a0a0a0',
                    maxWidth: '700px',
                  }}
                >
                  {service.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* How I Work */}
      <Box sx={{ backgroundColor: 'white', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 6,
              textTransform: 'uppercase',
              color: '#1a1a1a',
            }}
          >
            HOW I WORK
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '800px' }}>
            {[
              { step: '1', title: 'Discovery Call', desc: 'We discuss your project, stack, and timeline.' },
              { step: '2', title: 'Proposal', desc: 'I send a clear proposal with scope, timeline, and pricing.' },
              { step: '3', title: 'Kickoff', desc: 'I join your team or start the project. Daily standups, weekly reviews.' },
            ].map((item) => (
              <Box key={item.step} sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: '#dc143c',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 900,
                    fontSize: '1.25rem',
                    fontFamily: '"Arial Black", sans-serif',
                    flexShrink: 0,
                  }}
                >
                  {item.step}
                </Box>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '1.125rem', md: '1.25rem' },
                      fontWeight: 900,
                      fontFamily: '"Arial Black", sans-serif',
                      mb: 0.5,
                      color: '#1a1a1a',
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, color: '#666' }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Engagement Models */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 6,
              textTransform: 'uppercase',
              color: '#1a1a1a',
            }}
          >
            ENGAGEMENT MODELS
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 4,
            }}
          >
            {[
              {
                title: 'Staff Augmentation',
                price: '$75-100/hr',
                note: 'Full-time or part-time',
                desc: 'Join your team as senior AI architect.',
              },
              {
                title: 'Project-Based',
                price: 'Starting at $10,000',
                note: 'Fixed scope, fixed timeline',
                desc: 'Deliver a specific AI feature, API, or microservice.',
              },
              {
                title: 'Technical Advisory',
                price: '$75-100/hr',
                note: 'Flexible hours',
                desc: 'Architecture review, AI strategy, team mentoring.',
              },
            ].map((model) => (
              <Box
                key={model.title}
                sx={{
                  p: 4,
                  border: '2px solid #1a1a1a',
                  backgroundColor: 'white',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    fontWeight: 900,
                    fontFamily: '"Arial Black", sans-serif',
                    mb: 1,
                    color: '#1a1a1a',
                  }}
                >
                  {model.title}
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color: '#dc143c', mb: 0.5 }}>
                  {model.price}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#999',
                    mb: 3,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {model.note}
                </Typography>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1.7, color: '#666' }}>
                  {model.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ backgroundColor: 'white', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 6,
              textTransform: 'uppercase',
              color: '#1a1a1a',
            }}
          >
            FAQ
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '900px' }}>
            {[
              {
                q: 'How much does an AI architect cost in Peru?',
                a: 'Rates typically range from $75-100/hour for senior AI architecture work. Project-based engagements start at $10,000. Compared to US-based contractors at $200-350/hr, you get senior-level expertise at a significant cost advantage with full US timezone compatibility.',
              },
              {
                q: 'FastAPI vs Node.js: which is better for AI backends?',
                a: 'FastAPI wins for AI workloads. Python\'s ecosystem (LangChain, PyTorch, Transformers) is unmatched. FastAPI handles async I/O natively, has automatic OpenAPI docs, and integrates directly with ML pipelines. Node.js works for simple APIs but becomes painful when you need to run inference or process embeddings.',
              },
              {
                q: 'How long does it take to build a production RAG feature?',
                a: 'A minimal viable RAG pipeline takes 2-3 weeks: document ingestion, chunking strategy, embedding model selection, vector DB setup, retrieval logic, and LLM integration. A production-ready system with caching, monitoring, and error handling takes 4-6 weeks.',
              },
              {
                q: 'What is RAG and why does my startup need it?',
                a: 'RAG (Retrieval-Augmented Generation) lets your AI answer questions from YOUR data — not just generic training data. Instead of hallucinating, it retrieves relevant documents and grounds responses in facts. Essential for customer support bots, internal knowledge bases, and document analysis tools.',
              },
              {
                q: 'Do you work with US timezone?',
                a: 'Yes. I\'m based in Peru (GMT-5) which aligns with US Eastern and Central time. I join daily standups, participate in sprint planning, and maintain real-time Slack/Teams communication with US teams.',
              },
              {
                q: 'How do I hire a Python consultant for an LLM project?',
                a: 'Start with a 15-minute discovery call to discuss your use case, existing stack, and timeline. I\'ll send a proposal within 48 hours with scope, milestones, and pricing. Kickoff happens within a week. You own all IP, and billing is simple B2B invoicing.',
              },
            ].map((faq, i) => (
              <Box key={i} sx={{ borderBottom: '2px solid #e0e0e0', pb: 3 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    fontWeight: 900,
                    fontFamily: '"Arial Black", sans-serif',
                    mb: 1,
                    color: '#1a1a1a',
                  }}
                >
                  {faq.q}
                </Typography>
                <Typography sx={{ fontSize: '1rem', lineHeight: 1.7, color: '#666' }}>
                  {faq.a}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#c41e3a',
          color: 'white',
          py: { xs: 10, md: 15 },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          backgroundImage: 'linear-gradient(135deg, #c41e3a 0%, #8b1429 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            zIndex: 1,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ maxWidth: '900px', mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3.5rem' },
                fontWeight: 900,
                fontFamily: '"Impact", sans-serif',
                mb: 4,
                textTransform: 'uppercase',
                lineHeight: 1.1,
              }}
            >
              CURRENT AVAILABILITY
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              {currentMonth} — Open for select projects.
            </Typography>

            <Typography sx={{ fontSize: '1.125rem', mb: 6 }}>
              kevyn@franco.expert
            </Typography>

            <Button
              href="mailto:kevyn@franco.expert"
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: 'white',
                color: '#1a1a1a',
                px: 5,
                py: 1.5,
                fontSize: '1.125rem',
                fontWeight: 700,
                borderRadius: 0,
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
            >
              Get in Touch
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ConsultingPage;
